import React from 'react'
import { makeStyles, alpha } from '@material-ui/core/styles'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { userRoleSelector } from 'store/auth/selectors'

import {
  Typography,
  IconButton,
  SvgIcon,
  MenuItem,
  Menu,
  CircularProgress,
} from '@material-ui/core'
import { Menu as MenuIcon, InfoOutlined, OpenInNew } from '@material-ui/icons'
import { ReactComponent as AiIcon } from 'assets/svg/ai_icon.svg'

import { Epub3Reader } from './epub3-reader.component'
import { QuizReader } from './quizzes-reader.component'
import ReaderHTML5 from './html5-reader.component'
import ReaderIWB from './iwb-reader.component'
import ReaderDigiIWB from './digiiwb-reader.component'
import ReaderDigiIebook from './digiiebook-reader.component'
import { ReaderDigipub } from './digipub-reader.component'
import { useReaderContext } from './context/reader-context'
import { ReaderLoader } from './reader-loader.component'
import { Scorm12Reader } from './Scorm12Reader'
import {
  useLaunchLudenso,
  useLudensoRemainings,
} from 'api/SchoolFeatures/Ludenso/queries'

import roles from 'constants/roles'
import useNotification from 'hooks/useNotification'
import parseError from 'helpers/errorHelpers'

const useStyles = makeStyles((theme) => ({
  cosmosAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginRight: theme.spacing(1),
    cursor: 'pointer',
  },
  floatingCosmosButton: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: theme.zIndex.speedDial || 1050,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    boxShadow: 'none',
    padding: 0,
    transition: 'transform 0.2s ease',
    '&:hover': {
      backgroundColor: 'transparent',
      transform: 'scale(1.1)',
    },
  },
  cosmosMenuPaper: {
    marginTop: '-16px !important',
    borderRadius: '16px !important',
    minWidth: 260,
    overflow: 'hidden',
    boxShadow:
      '0 12px 30px -4px rgba(0, 0, 0, 0.15), 0 4px 12px -2px rgba(0, 0, 0, 0.08) !important',
    border: '1px solid rgba(0, 0, 0, 0.08)',
  },
  cosmosMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid rgba(0, 0, 0, 0.07)',
    cursor: 'default',
    outline: 'none',
  },
  cosmosMenuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
    },
  },
  tokenBadge: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    padding: '3px 9px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 12,
    marginLeft: 8,
  },
}))

export const ReaderContent = () => {
  const {
    showContent,
    info: { pkg, packageId, ludensoPackages = [] } = {},
    readerMode: {
      IS_EPUB,
      IS_HTML5,
      IS_DIGIPUB,
      IS_DIWB,
      IS_IWB,
      IS_DIGIIWB,
      IS_SCORM,
      IS_DIGIIEBOOK,
      IS_QUIZ,
    },
  } = useReaderContext()

  const classes = useStyles()
  const userRole = useSelector(userRoleSelector)
  const { addNotification } = useNotification()

  const [isLaunchingLudenso, setIsLaunchingLudenso] = useState(false)
  const launchLudenso = useLaunchLudenso()
  const [cosmosAnchorEl, setCosmosAnchorEl] = React.useState(null)
  const isCosmosMenuOpen = Boolean(cosmosAnchorEl)

  const isAllowedCosmosRole = useMemo(() => {
    return [
      roles.ADMIN,
      roles.TEACHER,
      roles.INDTEACHER,
      roles.MASTER,
      roles.SCHOOLMANAGER,
    ].includes(userRole)
  }, [userRole])

  const isLudensoPackage = useMemo(() => {
    return (
      typeof pkg?.data?.LudensoPackage === 'object' &&
      pkg?.data?.LudensoPackage !== null &&
      Object.keys(pkg?.data?.LudensoPackage).length > 0
    )
  }, [pkg?.data?.LudensoPackage])

  const shouldFetchRemainings = isLudensoPackage && isAllowedCosmosRole

  const ludensoRemainings = useLudensoRemainings({
    queryOptions: {
      enabled: Boolean(shouldFetchRemainings),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  })

  const isLudensoActive = Boolean(ludensoRemainings.data?.active)

  const showCosmosIcon = useMemo(() => {
    return Boolean(shouldFetchRemainings && isLudensoActive)
  }, [shouldFetchRemainings, isLudensoActive])

  const needsRefetchRemainingsRef = React.useRef(false)

  const handleOpenLudenso = async () => {
    if (isLaunchingLudenso) return
    setIsLaunchingLudenso(true)
    try {
      const currentLudensoId = pkg?.data?.LudensoPackage?.ludenso_id ?? null

      const ludensoIds = (ludensoPackages || [])
        .map(
          (item) =>
            item?.Package?.LudensoPackage?.ludenso_id ??
            item?.LudensoPackage?.ludenso_id
        )
        .filter((id) => id !== null && id !== undefined && id !== '')

      if (
        currentLudensoId !== null &&
        currentLudensoId !== undefined &&
        !ludensoIds.includes(currentLudensoId)
      ) {
        ludensoIds.push(currentLudensoId)
      }

      const payload = {
        packages: Array.from(new Set(ludensoIds)),
        currentPackage: currentLudensoId,
      }

      const ludensoRes = await launchLudenso.mutateAsync(payload)

      if (typeof ludensoRes === 'string') {
        addNotification({
          type: 'error',
          message: parseError(ludensoRes),
        })
      } else {
        const baseUrl = ludensoRes?.url || ludensoRes?.data?.url
        if (baseUrl) {
          window.open(baseUrl, '_blank')
          needsRefetchRemainingsRef.current = true
        } else {
          addNotification({
            type: 'error',
            message: 'No launch URL received for Ludenso',
          })
        }
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: parseError(error, 'Error launching Ludenso'),
      })
    } finally {
      setIsLaunchingLudenso(false)
    }
  }

  const handleCosmosMenuOpen = (event) => {
    setCosmosAnchorEl(event.currentTarget)
    if (needsRefetchRemainingsRef.current) {
      needsRefetchRemainingsRef.current = false
      ludensoRemainings.refetch()
    }
  }

  const handleCosmosMenuClose = () => {
    setCosmosAnchorEl(null)
  }

  const cosmosMenuId = 'cosmos-account-menu'

  const remainingCount = useMemo(() => {
    const raw =
      ludensoRemainings.data?.remaining ??
      ludensoRemainings.data?.remainings ??
      (typeof ludensoRemainings.data === 'number' ? ludensoRemainings.data : 0)
    return Number(raw) || 0
  }, [ludensoRemainings.data])

  const renderCosmosMenu = (
    <Menu
      anchorEl={cosmosAnchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={cosmosMenuId}
      keepMounted
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={isCosmosMenuOpen}
      onClose={handleCosmosMenuClose}
      classes={{ paper: classes.cosmosMenuPaper }}
    >
      <div className={classes.cosmosMenuHeader}>
        {ludensoRemainings.isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={16} color="primary" />
            <Typography variant="body2">Loading...</Typography>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontWeight: 500 }}
              >
                Remaining Tokens:
              </Typography>
              <span className={classes.tokenBadge}>{remainingCount}</span>
            </div>
            <InfoOutlined
              color="action"
              style={{ fontSize: 18, marginLeft: 8, opacity: 0.7 }}
            />
          </div>
        )}
      </div>
      <MenuItem
        onClick={() => {
          handleCosmosMenuClose()
          handleOpenLudenso()
        }}
        disabled={
          isLaunchingLudenso ||
          ludensoRemainings.isLoading ||
          remainingCount <= 0
        }
        className={classes.cosmosMenuItem}
      >
        <span>Launch AI Assistant</span>
        <OpenInNew
          color="primary"
          style={{ fontSize: 18, marginLeft: 12, opacity: 0.8 }}
        />
      </MenuItem>
    </Menu>
  )

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      <ReaderLoader />
      {showContent ? (
        <>
          {IS_EPUB || IS_DIWB ? (
            <Epub3Reader />
          ) : IS_HTML5 ? (
            <ReaderHTML5
            // title={pkg.data?.title} url={readPackage.data?.url}
            />
          ) : IS_DIGIPUB ? (
            <ReaderDigipub />
          ) : IS_IWB ? (
            <ReaderIWB />
          ) : IS_QUIZ ? (
            <QuizReader />
          ) : IS_DIGIIWB ? (
            <ReaderDigiIWB />
          ) : IS_DIGIIEBOOK ? (
            <ReaderDigiIebook />
          ) : null}
          {IS_SCORM ? (
            // <ScormProvider
            //   version="1.2"
            //   debug={process.env.NODE_ENV !== 'production'}
            // >
            <Scorm12Reader />
          ) : null}
        </>
      ) : null}
      {renderCosmosMenu}
      {showCosmosIcon && (
        <IconButton
          onClick={handleCosmosMenuOpen}
          disabled={isLaunchingLudenso}
          aria-controls={cosmosMenuId}
          className={classes.floatingCosmosButton}
          aria-label="AI Assistant"
        >
          {isLaunchingLudenso ? (
            <CircularProgress size={28} color="inherit" />
          ) : (
            <SvgIcon
              color="inherit"
              component={AiIcon}
              viewBox="0 0 24 24"
              style={{ width: 44, height: 44, fontSize: 44 }}
            />
          )}
        </IconButton>
      )}
    </div>
  )
}
